import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Play } from "lucide-react";

const SENTENCES = [
  "the quick brown fox jumps over the lazy dog",
  "neon lights flicker as the arcade comes alive at night",
  "press start to begin your epic adventure right now",
  "level up your skills and climb the global leaderboard",
  "pixels and power ups define the golden age of gaming",
  "a true gamer never gives up no matter how hard the boss",
];

export function TypingTest({ onScore }) {
  const [target, setTarget] = useState("");
  const [input, setInput] = useState("");
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);
  const [startTime, setStartTime] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const inputRef = useRef(null);
  const reported = useRef(false);

  const start = () => {
    setTarget(SENTENCES[Math.floor(Math.random() * SENTENCES.length)]);
    setInput(""); setDone(false); setRunning(true); setStartTime(Date.now()); setElapsed(0);
    reported.current = false;
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  useEffect(() => {
    if (!running || done) return;
    const id = setInterval(() => setElapsed((Date.now() - startTime) / 1000), 100);
    return () => clearInterval(id);
  }, [running, done, startTime]);

  const onChange = (e) => {
    if (!running || done) return;
    const val = e.target.value;
    setInput(val);
    if (val === target) {
      setDone(true); setRunning(false);
    }
  };

  const words = target ? target.trim().split(" ").length : 0;
  const minutes = Math.max(elapsed, 1) / 60;
  const wpm = Math.min(250, minutes > 0 ? Math.round(words / minutes) : 0);
  let correct = 0;
  for (let i = 0; i < input.length; i++) if (input[i] === target[i]) correct++;
  const accuracy = input.length ? Math.round((correct / input.length) * 100) : 100;

  useEffect(() => {
    if (done && !reported.current) {
      reported.current = true;
      onScore?.(Math.round(wpm * (accuracy / 100)) * 10);
    }
  }, [done, wpm, accuracy, onScore]);

  return (
    <div className="w-full max-w-xl mx-auto" data-testid="typing-test">
      <div className="flex items-center justify-center gap-6 mb-5 font-mono">
        <span className="text-neon-cyan text-lg">WPM: {wpm}</span>
        <span className="text-neon-lime text-lg">ACC: {accuracy}%</span>
        <span className="text-neon-yellow text-lg">⏱ {elapsed.toFixed(1)}s</span>
      </div>

      {!running && !done && (
        <div className="text-center py-10">
          <Button onClick={start} data-testid="typing-start" className="bg-neon-cyan text-black rounded-none font-bold uppercase tracking-widest h-12 px-8">
            <Play className="w-5 h-5 mr-2 fill-black" /> Start Typing Test
          </Button>
        </div>
      )}

      {(running || done) && (
        <>
          <div className="p-5 rounded-xl border-2 border-zinc-700 bg-zinc-900 mb-4 text-xl leading-relaxed font-mono">
            {target.split("").map((ch, i) => {
              let color = "#71717a";
              if (i < input.length) color = input[i] === ch ? "#39FF14" : "#FF003C";
              return <span key={i} style={{ color, background: i === input.length ? "#00F0FF33" : "transparent" }}>{ch}</span>;
            })}
          </div>
          <input ref={inputRef} value={input} onChange={onChange} disabled={done} data-testid="typing-input"
            placeholder="Start typing here…" autoFocus
            className="w-full bg-zinc-950 border-2 border-zinc-700 focus:border-neon-cyan outline-none rounded-lg px-4 py-3 font-mono text-white" />
        </>
      )}

      {done && (
        <div className="text-center mt-6" data-testid="typing-result">
          <p className="font-display font-black text-3xl text-neon-lime uppercase">{wpm} WPM!</p>
          <p className="text-zinc-400 mt-1">{accuracy}% accuracy in {elapsed.toFixed(1)}s</p>
          <Button onClick={start} className="mt-4 bg-neon-cyan text-black rounded-none font-bold uppercase tracking-widest">Try Again</Button>
        </div>
      )}
    </div>
  );
}
